import { Star, MapPin, Clock, Heart, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { memo } from "react";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import type { CourseWithTrail } from "@/types";
import { useRecommendation } from "@/hooks/useRecommendation";

interface CourseCardProps {
  course: CourseWithTrail;
  index?: number;
}

const CourseCard = memo(({ course, index = 0 }: CourseCardProps) => {
  const { handleRecommendation, isLoading, isLoadingCourse } = useRecommendation();
  const shouldReduceMotion = useReducedMotion();
  const isCurrentCardLoading = isLoadingCourse(course.course_id);

  const getDifficultyLevel = (difficulty: string | null): number => {
    if (!difficulty) return 2;
    switch (difficulty) {
      case "쉬움":
        return 1;
      case "보통":
        return 2;
      case "어려움":
        return 3;
      default:
        return 2;
    }
  };

  const renderDifficultyStars = (level: number) => {
    return Array.from({ length: 3 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < level ? "fill-[#F39800] text-[#F39800]" : "text-gray-300"
        }`}
      />
    ));
  };

  const formatDuration = (minutes: number | null): string => {
    if (!minutes) return "정보 없음";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
    }
    return `${mins}분`;
  };

  const formatDistance = (km: number | null): string => {
    if (!km) return "정보 없음";
    return `${km}km`;
  };

  const getImageUrl = (): string => {
    if (course.main_photo_url) {
      return course.main_photo_url;
    }
    // 기본 이미지 URL들
    const defaultImages = [
      "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=2000&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1615729947596-a598e5de0ab3?q=80&w=2000&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1439886183900-e79ec0057170?q=80&w=2000&auto=format&fit=crop",
    ];
    return defaultImages[parseInt(course.course_id.slice(-1)) % defaultImages.length];
  };

  // Animation variants
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : index * 0.1,
        ease: "easeOut" as const,
      },
    },
  };

  const heartVariants = {
    initial: { scale: 1 },
    tap: { scale: 0.85 },
    hover: { 
      scale: isCurrentCardLoading ? 1 : 1.1,
      transition: { duration: 0.2 }
    },
    loading: {
      scale: 1,
      rotate: 360,
      transition: {
        rotate: {
          duration: 1,
          repeat: Infinity,
          ease: "linear"
        }
      }
    }
  };

  const heartIconVariants = {
    recommended: {
      scale: [1, 1.3, 1],
      transition: {
        duration: 0.4,
        ease: "easeInOut"
      }
    },
    unrecommended: {
      scale: [1, 0.8, 1],
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    pulse: {
      scale: [1, 1.1, 1],
      opacity: [1, 0.8, 1],
      transition: {
        duration: 0.6,
        ease: "easeInOut"
      }
    }
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      <motion.div 
        className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 group relative ${
          isCurrentCardLoading ? 'opacity-90 pointer-events-none' : ''
        }`}
        whileHover={{ y: shouldReduceMotion ? 0 : (isCurrentCardLoading ? 0 : -5) }}
        transition={{ duration: 0.2 }}
        animate={{
          scale: isCurrentCardLoading ? 0.98 : 1,
          opacity: isCurrentCardLoading ? 0.9 : 1
        }}
      >
        {/* Image Container */}
        <div className="relative h-48 overflow-hidden">
          <Link to={`/course-detail/${course.course_id}`}>
            <motion.img
              src={getImageUrl()}
              alt={course.name}
              className="w-full h-full object-cover cursor-pointer"
              whileHover={{ scale: shouldReduceMotion ? 1 : 1.05 }}
              transition={{ duration: 0.3 }}
            />
          </Link>
          
          {/* Trail Name Badge - Inside SmartLink area but positioned absolutely */}
          <motion.div 
            className="absolute top-3 left-3 bg-[#F39800] text-white px-2 py-1 rounded-full text-sm font-semibold pointer-events-none z-10"
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
          >
            {course.trail_name}
          </motion.div>
          
          {/* Heart Button - OUTSIDE SmartLink, positioned absolutely with high z-index */}
          <motion.button
            onClick={(e) => {
              console.log('🔥 하트 버튼 클릭됨:', { 
                courseId: course.course_id, 
                isCurrentlyRecommended: course.is_recommended || false 
              });
              e.preventDefault();
              e.stopPropagation();
              handleRecommendation(course.course_id, course.is_recommended || false);
            }}
            disabled={isCurrentCardLoading}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all duration-200 z-20 ${
              isCurrentCardLoading 
                ? "bg-gray-200 cursor-not-allowed" 
                : "bg-white/80 hover:bg-white shadow-sm hover:shadow-md"
            }`}
            variants={heartVariants}
            initial="initial"
            animate={isCurrentCardLoading ? "loading" : "initial"}
            whileHover={!isCurrentCardLoading ? "hover" : "loading"}
            whileTap={!isCurrentCardLoading ? "tap" : "loading"}
            style={{ pointerEvents: 'auto' }}
          >
            {/* 단순화된 하트 아이콘 표시 */}
            {isCurrentCardLoading ? (
              <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
            ) : (
              <Heart
                className={`w-5 h-5 transition-colors ${
                  course.is_recommended 
                    ? "fill-red-500 text-red-500" 
                    : "text-gray-600 hover:text-red-400"
                }`}
              />
            )}
          </motion.button>
        </div>

        {/* Content */}
        <Link to={`/course-detail/${course.course_id}`}>
          <div className="p-5 cursor-pointer">
            <h4 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1">
              {course.name}
            </h4>
            
            <div className="flex items-center mb-3">
              <span className="text-sm text-gray-600 mr-2">난이도:</span>
              <div className="flex items-center">
                {renderDifficultyStars(getDifficultyLevel(course.difficulty))}
                <span className="text-sm text-gray-600 ml-2">{course.difficulty || "정보 없음"}</span>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
              {course.short_desc || course.description || "코스 설명이 없습니다."}
            </p>

            <div className="flex items-center text-sm text-gray-500 mb-2">
              <MapPin className="w-4 h-4 mr-1" />
              <span className="mr-4 truncate">{course.start_point || "출발지 정보 없음"}</span>
            </div>
            
            <div className="flex items-center text-sm text-gray-500 mb-4">
              <Clock className="w-4 h-4 mr-1" />
              <span className="mr-4">{formatDuration(course.estimated_time)}</span>
              <span>거리: {formatDistance(course.length)}</span>
            </div>

            {/* 추천 수 표시 */}
            <AnimatePresence>
              {course.recommend_count > 0 && (
                <motion.div 
                  className="flex items-center justify-center mb-3 text-sm text-gray-500"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  layout
                >
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 0.3 }}
                  >
                    <Heart className="w-4 h-4 fill-red-500 text-red-500 mr-1" />
                  </motion.div>
                  <motion.span
                    key={course.recommend_count}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    {course.recommend_count}명이 추천했습니다
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="text-center pt-3 border-t">
              <span className="text-[#F39800] font-semibold hover:text-[#E08700] transition-colors">
                자세히 보기 →
              </span>
            </div>
          </div>
        </Link>
      </motion.div>
    </motion.div>
  );
}, (prevProps: CourseCardProps, nextProps: CourseCardProps) => {
  // Custom comparison function for React.memo
  // Only re-render if course data has actually changed
  return (
    prevProps.course.course_id === nextProps.course.course_id &&
    prevProps.course.name === nextProps.course.name &&
    prevProps.course.is_recommended === nextProps.course.is_recommended &&
    prevProps.course.recommend_count === nextProps.course.recommend_count &&
    prevProps.course.length === nextProps.course.length &&
    prevProps.course.estimated_time === nextProps.course.estimated_time &&
    prevProps.course.difficulty === nextProps.course.difficulty &&
    prevProps.course.start_point === nextProps.course.start_point &&
    prevProps.course.short_desc === nextProps.course.short_desc &&
    prevProps.course.description === nextProps.course.description &&
    prevProps.course.main_photo_url === nextProps.course.main_photo_url &&
    prevProps.course.trail_name === nextProps.course.trail_name
  );
});

CourseCard.displayName = 'CourseCard';

export default CourseCard;
